import { IconAlertCircle } from '@tabler/icons-react';
import { FinchConnectionStatus, FinchSyncStatus } from '@zorro/clients';
import { Card } from '@zorro/design-system/v2/Card';
import { Divider } from '@zorro/design-system/v2/Divider';
import {
  formatDateTimeEnLocaleFromUtc,
  getNow,
  parseDateTimeISO,
  tryParseDateISO,
} from '@zorro/shared/formatters';
import {
  Box,
  Group,
  Icon,
  Image,
  Stack,
  Text,
  brand,
} from '@zorro/web/design-system';
import { useMonolithQuery, useRoles } from '@zorro/web/ui-utils';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { $enum } from 'ts-enum-util';

import { FinchConnect } from './FinchConnect';
import { FinchConnectionCardDotsMenu } from './FinchConnectionCardDotsMenu';

dayjs.extend(utc);
dayjs.extend(timezone);

type StatusConfigType =
  | {
      status: string;
      statusColor: string | null;
      getDescription?: (providerName: string) => string;
      descriptionBoxColor?: string;
      shouldShowNextScheduledSync: boolean;
      shouldShowFixConnection: boolean;
      shouldShowFooter: boolean;
    }
  | undefined;

export const getFinchConnectionStatusConfig = (
  connectionStatus: FinchConnectionStatus,
  lastSyncStatus: FinchSyncStatus | undefined | null,
  earliestEnrollmentsSyncDate?: string
): StatusConfigType => {
  const earliestEnrollmentsSync = tryParseDateISO(earliestEnrollmentsSyncDate);

  if (
    lastSyncStatus === FinchSyncStatus.FAILURE &&
    earliestEnrollmentsSync &&
    getNow().isSameOrAfter(earliestEnrollmentsSync)
  ) {
    return {
      status: 'Failed to sync',
      statusColor: brand.zorroFire700,
      getDescription: (providerName: string) =>
        `Last sync with ${providerName} failed. Please contact our support team for assistance`,
      descriptionBoxColor: brand.zorroFire300,
      shouldShowNextScheduledSync: true,
      shouldShowFixConnection: false,
      shouldShowFooter: true,
    };
  }

  return $enum.mapValue(connectionStatus).with<StatusConfigType>({
    [FinchConnectionStatus.CONNECTED]: {
      status: 'Connected',
      statusColor: brand.zorroGreen600,
      shouldShowNextScheduledSync: true,
      shouldShowFixConnection: false,
      shouldShowFooter: true,
    },
    [FinchConnectionStatus.PENDING]: {
      status: 'Pending initial connection',
      statusColor: brand.zorroYolk800,
      getDescription: () =>
        'Initial connection configuration in progress (typically takes 1–2 weeks)',
      descriptionBoxColor: brand.zorroYolk200,
      shouldShowNextScheduledSync: true,
      shouldShowFixConnection: false,
      shouldShowFooter: false,
    },
    [FinchConnectionStatus.ERROR]: {
      status: 'Failed to connect',
      statusColor: brand.zorroFire700,
      getDescription: (providerName: string) =>
        `Failed to connect with ${providerName}. Please contact our support team for assistance`,
      descriptionBoxColor: brand.zorroFire300,
      shouldShowNextScheduledSync: true,
      shouldShowFixConnection: false,
      shouldShowFooter: false,
    },
    [FinchConnectionStatus.REAUTHENTICATE]: {
      status: 'Connection lost',
      statusColor: brand.zorroFire700,
      getDescription: (providerName: string) =>
        `Connection with ${providerName} was lost. Please try restoring the connection or contact our support team for assistance`,
      descriptionBoxColor: brand.zorroFire300,
      shouldShowNextScheduledSync: false,
      shouldShowFixConnection: true,
      shouldShowFooter: true,
    },
    [FinchConnectionStatus.DISCONNECTED]: undefined,
  });
};

type Props = {
  employerId: string;
};

export const FinchConnectionCard = ({ employerId }: Props) => {
  const { isZorroOperations, isEmployerAdmin } = useRoles();

  const { data: finchEmployer, isLoading: isLoadingEmployer } =
    useMonolithQuery({
      method: 'finchControllerFindEmployer',
      params: employerId && [employerId],
    });
  const { data: finchProvider, isLoading: isLoadingProvider } =
    useMonolithQuery({
      method: 'finchControllerGetFinchProvider',
      params: finchEmployer?.providerId && [finchEmployer.providerId],
    });

  if (isLoadingEmployer || isLoadingProvider || !finchEmployer) {
    return null;
  }

  const { connectionStatus, lastSyncStatus, earliestEnrollmentsSyncDate } =
    finchEmployer;
  const lastSuccessfulSyncDateTime = finchEmployer?.lastSuccessfulSyncDateTime
    ? parseDateTimeISO(finchEmployer?.lastSuccessfulSyncDateTime)
    : undefined;
  const nextSyncDate = getNow().add(1, 'day').startOf('day');

  const {
    status,
    statusColor,
    getDescription,
    descriptionBoxColor,
    shouldShowNextScheduledSync,
    shouldShowFixConnection,
    shouldShowFooter,
  } = connectionStatus
    ? getFinchConnectionStatusConfig(
        connectionStatus,
        lastSyncStatus,
        earliestEnrollmentsSyncDate
      ) || {}
    : {};

  return (
    <Card maw="60rem" p="xl" variant="white-border">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Group wrap="nowrap" gap="xl">
            <Card maw="11.5rem" p="md" variant="white-border">
              <Image src={finchProvider?.logoUrl} alt="Provider" />
            </Card>
            <Stack gap="0.25rem">
              <Text fw={600} size="md">
                {finchProvider?.displayName}
              </Text>
              <Text size="sm">
                <Text size="sm" c="zorroGray.4" mr="xs">
                  Status:
                </Text>
                {statusColor && (
                  <Box
                    style={{ borderRadius: '100%' }}
                    display="inline-block"
                    bg={statusColor}
                    mr={6}
                    w={10}
                    h={10}
                  />
                )}
                {status}
              </Text>
            </Stack>
          </Group>
          {(isZorroOperations || isEmployerAdmin) && (
            <FinchConnectionCardDotsMenu
              finchProviderDisplayName={finchProvider?.displayName || ''}
              employerId={employerId}
              finchEmployerId={finchEmployer.id}
            />
          )}
        </Group>
        {getDescription && descriptionBoxColor && (
          <Group
            wrap="nowrap"
            align="flex-start"
            gap="xs"
            p="sm"
            bg={descriptionBoxColor}
            style={(theme) => ({
              borderRadius: theme.radius.md,
            })}
          >
            <Icon
              icon={IconAlertCircle}
              size="1.25rem"
              style={{ flexShrink: 0 }}
            />
            <Text>{getDescription(finchProvider?.displayName || '')}</Text>
          </Group>
        )}
      </Stack>
      {shouldShowFooter && (
        <>
          <Divider my="md" />
          <Group wrap="nowrap" gap="xl" justify="space-between">
            <Text size="md">
              <Text size="md" c="zorroGray.4" mr="xs">
                Last synced:
              </Text>
              {lastSuccessfulSyncDateTime
                ? formatDateTimeEnLocaleFromUtc(lastSuccessfulSyncDateTime)
                : '-'}
            </Text>
            <Group gap="xs">
              <Text size="md" mr="sm">
                <Text size="md" c="zorroGray.4" mr="xs">
                  Next scheduled sync:
                </Text>
                {shouldShowNextScheduledSync
                  ? formatDateTimeEnLocaleFromUtc(nextSyncDate)
                  : '-'}
              </Text>
              {shouldShowFixConnection && (
                <FinchConnect employerId={employerId} isFixConnectionMode />
              )}
            </Group>
          </Group>
        </>
      )}
    </Card>
  );
};
